import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  QuestMetadata,
  Obelisk,
  Compilation,
  User,
  GeoCoords,
  transformQuestFromServer,
  transformObeliskFromServer,
  transformCompilationFromServer,
  EnvProviderService
} from 'quest-atlas-angular-components';
import { UserService } from './user.service';

export enum EventType {
  Quest = 'quest',
  Obelisk = 'obelisk',
  Compilation = 'compilation'
}

export enum EventVisibility {
  Public = 'public',
  Contacts = 'contacts',
  Private = 'private'
}

export enum EventAccess {
  Free = 'free',
  Paid = 'paid',
  Approval = 'approval',
  ApprovalPaid = 'approval_paid'
}

export enum StartType {
  Auto = 'auto',
  Manual = 'manual'
}

export interface EventAdmin {
  id: number;
  userId: number;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  addedAt: Date;
}

export interface OrganizerDetails {
  userId: number;
  username: string;
  firstName?: string;
  lastName?: string;
  avatarConfig?: any;
}

export interface CompilationMilestone {
  percentage: number;
  name?: string;
}

export interface MilestoneProgress {
  name: string;
  percentage: number;
  reached: boolean;
  reachedAt: Date | null;
  currentProgress: number;
}

export interface UserEvent {
  id?: number;
  name?: string;
  description?: string;
  startDate?: Date;
  endDate?: Date;
  registrationEndDate?: Date;
  type: EventType;
  visibility: EventVisibility;
  access: EventAccess;
  startType: StartType;
  slots?: number;
  started?: boolean;
  completed?: boolean;
  organizer?: number;
  organizerDetails?: OrganizerDetails;
  adminsList?: EventAdmin[];
  isAdmin?: boolean;
  mine?: boolean;
  quests?: QuestMetadata[];
  obelisks?: Obelisk[];
  compilations?: Compilation[];
  config?: { compilationMilestones?: CompilationMilestone[] };
  rewards?: { exp?: number; tokens?: number };
  createdAt?: Date;
  updatedAt?: Date;
  eventAccessToken?: string;
}

export interface ParticipantUser extends User {
  metadata?: any;
  milestoneProgress?: MilestoneProgress[];
}

export interface EventsLoadParams {
  userLocation?: GeoCoords;
  tabName?: string;
}

@Injectable({
  providedIn: 'root'
})
export class EventsService {
  private baseUrl = `${environment.API_URL}/events`;

  constructor(private http: HttpClient, private userService: UserService, private env: EnvProviderService) {}

  // Event CRUD Operations
  getEvents$(params?: EventsLoadParams): Observable<UserEvent[]> {
    let httpParams = new HttpParams();

    if (params?.tabName) {
      httpParams = httpParams.set('tab_name', params.tabName);
    }

    if (params?.userLocation) {
      httpParams = httpParams.set('user_location', `${params.userLocation.lng},${params.userLocation.lat}`);
    }

    return this.http.get<any[]>(this.baseUrl, { params: httpParams }).pipe(map((events) => events.map((e) => this.transformEventFromBackend(e))));
  }

  getEvent$(eventId: number, eventAccessToken?: string): Observable<UserEvent> {
    let httpParams = new HttpParams();
    if (eventAccessToken) {
      httpParams = httpParams.set('event_access_token', eventAccessToken);
    }

    return this.http.get<any>(`${this.baseUrl}/${eventId}`, { params: httpParams }).pipe(map((e) => this.transformEventFromBackend(e)));
  }

  createEvent$(event: UserEvent): Observable<UserEvent> {
    const body = this.transformEventToBackend(event);
    return this.http.post<any>(`${this.baseUrl}/`, body).pipe(map((e) => this.transformEventFromBackend(e)));
  }

  updateEvent$(event: UserEvent): Observable<UserEvent> {
    const body = this.transformEventToBackend(event);
    return this.http.put<any>(`${this.baseUrl}/${event.id}`, body).pipe(map((e) => this.transformEventFromBackend(e)));
  }

  deleteEvent$(event: UserEvent): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/${event.id}`);
  }

  getEventsByCompilation$(compilationId: number): Observable<UserEvent[]> {
    return this.http
      .get<any>(`${this.baseUrl}/by-compilation/${compilationId}`)
      .pipe(map((response) => response.events.map((e) => this.transformEventFromBackend(e))));
  }

  getEventsByQuest$(questId: number): Observable<UserEvent[]> {
    return this.http
      .get<any>(`${this.baseUrl}/by-quest/${questId}`)
      .pipe(map((response) => response.events.map((e) => this.transformEventFromBackend(e))));
  }

  // Participant Management
  addUserToEvent$(event: UserEvent, userId: number): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/${event.id}/add-user`, { user_id: userId });
  }

  removeUserFromEvent$(event: UserEvent, userId: number): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/${event.id}/remove-user/${userId}`);
  }

  requestToJoinEvent$(event: UserEvent): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/${event.id}/request-to-join`, {});
  }

  joinRequests$(eventId: number): Observable<User[]> {
    return this.http.get<User[]>(`${this.baseUrl}/${eventId}/requests-list`);
  }

  acceptJoinRequest$(eventId: number, userId: number): Observable<unknown> {
    return this.http.patch(`${this.baseUrl}/${eventId}/accept-join-request/${userId}`, {});
  }

  rejectJoinRequest$(eventId: number, userId: number): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/${eventId}/reject-join-request/${userId}`);
  }

  joinEvent$(eventId: number): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/${eventId}/join`, {});
  }

  leaveEvent$(eventId: number): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/${eventId}/leave`);
  }

  haveIRequestedToJoinEvent$(eventId: number): Observable<boolean> {
    return this.http.get<{ result: boolean }>(`${this.baseUrl}/${eventId}/have-i-join-request-pending`).pipe(map((response) => response.result));
  }

  getParticipants$(eventId: number, eventAccessToken?: string): Observable<ParticipantUser[]> {
    let httpParams = new HttpParams();
    if (eventAccessToken) {
      httpParams = httpParams.set('event_access_token', eventAccessToken);
    }

    return this.http
      .get<any[]>(`${this.baseUrl}/${eventId}/participants`, { params: httpParams })
      .pipe(map((users) => users.map((u) => this.transformParticipantFromBackend(u))));
  }

  getParticipantsByStatus$(eventId: number, status: string, eventAccessToken?: string): Observable<ParticipantUser[]> {
    let httpParams = new HttpParams();
    if (eventAccessToken) {
      httpParams = httpParams.set('event_access_token', eventAccessToken);
    }

    return this.http
      .get<any[]>(`${this.baseUrl}/${eventId}/participants-by-progress-state/${status}`, { params: httpParams })
      .pipe(map((users) => users.map((u) => this.transformParticipantFromBackend(u))));
  }

  /**
   * Gets milestone summary for admins - shows who reached each milestone, sorted by reached_at time
   * Returns a Map where keys are milestone names and values are arrays of users who reached that milestone
   */
  getMilestoneSummary$(eventId: number, eventAccessToken?: string): Observable<Map<string, ParticipantUser[]>> {
    return this.getParticipants$(eventId, eventAccessToken).pipe(
      map((participants) => {
        const milestoneMap = new Map<string, ParticipantUser[]>();

        // Group participants by milestone
        participants.forEach((participant) => {
          if (participant.milestoneProgress) {
            participant.milestoneProgress.forEach((milestone) => {
              if (milestone.reached) {
                const key = milestone.name || milestone.percentage.toString();
                if (!milestoneMap.has(key)) {
                  milestoneMap.set(key, []);
                }
                milestoneMap.get(key)!.push(participant);
              }
            });
          }
        });

        // Sort each milestone's participants by reachedAt date (earliest first)
        milestoneMap.forEach((users, milestoneName) => {
          users.sort((a, b) => {
            const aMilestone = a.milestoneProgress?.find((m) => m.name === milestoneName || m.percentage.toString() === milestoneName);
            const bMilestone = b.milestoneProgress?.find((m) => m.name === milestoneName || m.percentage.toString() === milestoneName);

            if (!aMilestone?.reachedAt || !bMilestone?.reachedAt) return 0;

            return aMilestone.reachedAt.getTime() - bMilestone.reachedAt.getTime();
          });
        });

        return milestoneMap;
      })
    );
  }

  // Event Lifecycle
  startEvent$(eventId: number): Observable<unknown> {
    return this.http.patch(`${this.baseUrl}/${eventId}/start`, {});
  }

  completeEvent$(eventId: number): Observable<unknown> {
    return this.http.patch(`${this.baseUrl}/${eventId}/complete`, {});
  }

  getObtainedRewards$(eventId: number, eventAccessToken?: string): Observable<{ exp?: number; tokens?: number }> {
    let httpParams = new HttpParams();
    if (eventAccessToken) {
      httpParams = httpParams.set('event_access_token', eventAccessToken);
    }

    return this.http.get<{ exp?: number; tokens?: number }>(`${this.baseUrl}/${eventId}/obtained-rewards`, { params: httpParams });
  }

  // Admin Management
  addAdmin$(eventId: number, userId: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${eventId}/admins/add`, { user_id: userId });
  }

  removeAdmin$(eventId: number, adminId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${eventId}/admins/${adminId}/remove`);
  }

  // Sharing & Access
  generateBatchAccessCode$(eventId: number): Observable<{ code: string }> {
    return this.http.post<{ code: string }>(`${this.baseUrl}/${eventId}/batch-access-code`, {});
  }

  // Data Transformation
  private transformEventFromBackend(event: any): UserEvent {
    const user = this.userService.getLatestUserData();
    const isAdmin = user ? (event.admins_list || []).some((admin) => admin.user_id === user.id) : false;
    const isOrganizer = event.organizer === user?.id;

    return {
      ...event,
      mine: isAdmin || isOrganizer,
      startDate: event.start_date ? new Date(event.start_date) : undefined,
      endDate: event.end_date ? new Date(event.end_date) : undefined,
      registrationEndDate: event.registration_end_date ? new Date(event.registration_end_date) : undefined,
      startType: event.start_type,
      organizerDetails: event.organizer_details ? {
        userId: event.organizer_details.user_id,
        username: event.organizer_details.username,
        firstName: event.organizer_details.first_name,
        lastName: event.organizer_details.last_name,
        avatarConfig: event.organizer_details.avatar_config
      } : undefined,
      adminsList: event.admins_list?.map((admin: any) => ({
        id: admin.id,
        userId: admin.user_id,
        username: admin.username,
        email: admin.email,
        firstName: admin.first_name,
        lastName: admin.last_name,
        addedAt: new Date(admin.added_at)
      })),
      quests: (event.quests_list || []).map((rawQ) => transformQuestFromServer(rawQ, this.env.environment.API_URL)),
      obelisks: (event.obelisks_list || []).map((rawO) => transformObeliskFromServer(rawO)),
      compilations: (event.compilations_list || []).map((rawC: any) => transformCompilationFromServer(rawC, this.env.environment.API_URL)),
      createdAt: event.created_at ? new Date(event.created_at) : undefined,
      updatedAt: event.updated_at ? new Date(event.updated_at) : undefined,
      eventAccessToken: event.event_access_token
    };
  }

  private transformEventToBackend(event: UserEvent): any {
    const body: any = {
      name: event.name,
      description: event.description,
      type: event.type,
      visibility: event.visibility,
      access: event.access,
      start_type: event.startType,
      slots: event.slots,
      config: event.config
    };

    if (event.startDate) {
      body.start_date = event.startDate.toISOString();
    }

    if (event.endDate) {
      body.end_date = event.endDate.toISOString();
    }

    if (event.registrationEndDate) {
      body.registration_end_date = event.registrationEndDate.toISOString();
    }

    // Add content ID based on type
    if (event.type === EventType.Quest && event.quests?.[0]) {
      body.quest_id = event.quests[0].id;
    } else if (event.type === EventType.Obelisk && event.obelisks?.[0]) {
      body.obelisk_id = event.obelisks[0].uuid;
    } else if (event.type === EventType.Compilation && event.compilations?.[0]) {
      body.compilation_id = event.compilations[0].id;
    }

    return body;
  }

  private transformParticipantFromBackend(user: any): ParticipantUser {
    return {
      ...user,
      milestoneProgress: user.milestone_progress?.map((mp: any) => ({
        name: mp.name,
        percentage: mp.percentage,
        reached: mp.reached,
        reachedAt: mp.reached_at ? new Date(mp.reached_at) : null,
        currentProgress: mp.current_progress
      }))
    };
  }
}

